// The module shell's actions — Phase 05b. main.js's createModule / refreshModuleBody /
// closeModule and every place it removed a `.placed-module`, moved here. React renders
// the modules from the store (Canvas.tsx / PlacedModule.tsx); these are the only ways a
// module is added or removed, so React's tree and the DOM never disagree.
//
// main.js is gone since 05k-1 (its last piece here, the phone reorder handle, went with the
// mobile JS). The welcome screen is TS since 05i-2 (welcome.ts). The player picker is
// React + TS since 05c (PlayerStrip, player-actions.ts); the canvas padding since 05e-1
// (canvas-space.ts). A restore's `_`-state goes on before the first render since 05i-1.

import { flushSync } from "react-dom";
import { mintModuleId, type useProtoStore } from "../store/store.js";
import { snapshotPlayersOrNone } from "../data/snapshot.js";
import { buildPlayerStrip, playerIdFromSelect } from "./module-shell.js";
import { isSnapshotPlayer, type PlayerId } from "../data/players.js";
import { LIVE } from "../live/flag.js";
import { liveGoalkeeperFor } from "../live/default-players.js";
import { pickedOutsideSnapshot } from "../live/picked-players.js";
import { watchControlsOverflow } from "./controls-overflow.js";
import { createPlayerActions, installLinkHints, type PlayerActions } from "./player-actions.js";
import { getStickyPlayer } from "./sticky-player.js";
import { moduleElementOf, moduleIdOf as idOf } from "./module-registry.js";
import { selectedModules, selectionApi, type SelectionApi } from "./selection.js";
import { canvasSpaceApi, ensureCanvasPadding, installCanvasSpace, type CanvasSpaceApi } from "./canvas-space.js";
import { dismissWelcomeScreen } from "./welcome.js";
import { hideTooltip } from "./tooltip.js";
import { getModuleConfigSync, getSlug } from "../modules/module-config.js";
import { rendererFor } from "../modules/component-registry.js";
import { teamOfModule } from "../live/team.js";
import { isPanelModule, panelPartner, reattachPanel } from "./map-panels.js";
import { setModState } from "../modules/bridge.js";

type ProtoStore = typeof useProtoStore;

export interface CreateModuleOptions {
  /** px — the element's style.left / style.top. */
  x: number;
  y: number;
  cols: number;
  rows: number;
  title: string;
  pinned?: boolean;
  /** An explicit player's id (undo / preset); otherwise the sticky last pick. */
  player?: PlayerId | null;
  /** Put it first in the canvas (main.js's mobile prepend). */
  atStart?: boolean;
  /** `_`-state to restore (Undo, a preset, a saved dashboard, "All", a tile's context).
   *  Put on the element BEFORE the module's first render, so it renders once, already
   *  restored (05i-1, decision 2). main.js rendered first and then again with the state. */
  custom?: Record<string, unknown> | null;
}

/** The shell actions (the canvas hooks, the gestures and the tour call them). */
export interface CanvasApi extends PlayerActions, SelectionApi, CanvasSpaceApi {
  createModule: (opts: CreateModuleOptions) => HTMLElement;
  /** The close animation, then removal. Takes no checkpoint (the caller decides). */
  closeModule: (mod: HTMLElement) => void;
  /** Remove at once (the end of a merge's absorb animation). */
  removeModule: (mod: HTMLElement) => void;
  clearModules: () => void;
  refreshModuleBody: (mod: HTMLElement) => void;
  /** A genuine type switch (League Table → Squad): the new title re-renders the header. */
  setModuleTitle: (mod: HTMLElement, title: string) => void;
  /** The header pin button: one checkpoint, then flip the pin (with its pop on pinning). */
  togglePin: (mod: HTMLElement) => void;
  /** The header ✕: one checkpoint, then close. */
  closeFromHeader: (mod: HTMLElement) => void;
  /** The workspace shift (the store's): main.js's clearWorkspace resets it. */
  setWorkspaceShift: (x: number, y: number) => void;
  /** Delete / Backspace: close every selected module, ONE checkpoint for the batch.
   *  False (and nothing done) when nothing is selected. */
  deleteSelected: () => boolean;
}

/** What PlacedModule needs at its first render that isn't store data. */
export interface ShellInit {
  slug: string;
  /** The player picker's `<option>` list, or null for a module without the picker. */
  optionsHtml: string | null;
}

// Handed from createModule to PlacedModule's first render. Read (not taken) there, because
// StrictMode renders twice; createModule deletes it once React has committed.
const shellInits = new Map<string, ShellInit>();

export function shellInitFor(id: string): ShellInit {
  const init = shellInits.get(id);
  if (!init) throw new Error(`Module ${id} was added to the store directly — create modules through createModule`);
  return init;
}

let installed: CanvasApi | null = null;

/** The installed actions, for PlacedModule's pin / close handlers. */
export function canvasApi(): CanvasApi {
  if (!installed) throw new Error("installCanvas() hasn't run");
  return installed;
}

/** False during the loading screen — the canvas hooks listen from React's first render,
 *  before the actions exist (main.js's listeners only started once it loaded). */
export function isCanvasInstalled(): boolean {
  return installed !== null;
}

export function createCanvasApi(store: ProtoStore, saveState: () => void): CanvasApi {
  function refreshModuleBody(mod: HTMLElement): void {
    const titleEl = mod.querySelector<HTMLElement>(".module-title");
    const body = mod.querySelector<HTMLElement>(".module-body");
    if (!titleEl || !body) return;
    // Read off the element (React renders it from the store) — a module mid-removal may
    // still be refreshed by a late callback, as it could be under main.js.
    const title = titleEl.innerText;
    // Fade the body in, unless the caller asked for an in-place update (a player change).
    const fade = !(mod as unknown as { _skipBodyOpacity?: boolean })._skipBodyOpacity;
    if (fade) {
      body.style.transition = "none";
      body.style.opacity = "0";
      void body.offsetHeight; // commit opacity 0 before the transition comes back
      body.style.transition = "";
    }
    // modules.json names each title's renderer; the component registry maps it to the
    // widget's React mount (src/modules/bridge.tsx). An unknown title renders nothing, as
    // under main.js.
    const cfg = getModuleConfigSync(title);
    if (cfg) {
      const playerSelect = mod.querySelector<HTMLSelectElement>(".player-select");
      rendererFor(cfg.renderer)(body, playerSelect ? playerIdFromSelect(playerSelect.value) : null, cfg);
    }
    requestAnimationFrame(() => {
      body.style.opacity = "1";
      body.scrollTop = 0;
    });
  }

  function createModule(opts: CreateModuleOptions): HTMLElement {
    dismissWelcomeScreen();

    const cfg = getModuleConfigSync(opts.title);
    let player: PlayerId | null = null;
    let optionsHtml: string | null = null;
    if (cfg?.needsPlayer) {
      const players = snapshotPlayersOrNone();
      // Live only (ids-3): players picked from the real search that the snapshot lacks — so
      // Undo / the sticky pick can open this module on her. Empty on the snapshot site.
      const extras = LIVE ? pickedOutsideSnapshot((pid) => isSnapshotPlayer(players, pid)) : [];
      // Live: a Goalkeeping tile dragged in while the canvas is on the default player opens on
      // the default goalkeeper (the "All" button does the same).
      const explicit =
        opts.player ?? (LIVE && cfg.categoryId === "goalkeeping" ? liveGoalkeeperFor(getStickyPlayer()) : null);
      const strip = buildPlayerStrip(players, cfg.categoryId, explicit, getStickyPlayer(), extras);
      player = strip.initialPlayer ?? null;
      optionsHtml = strip.optionsHtml;
    }

    const id = mintModuleId();
    shellInits.set(id, { slug: getSlug(opts.title), optionsHtml });
    // Synchronous: main.js's callers use the returned element straight away
    // (Object.assign its `_`-state, refresh it).
    flushSync(() => {
      store.getState().addModule(
        {
          id,
          title: opts.title,
          x: opts.x,
          y: opts.y,
          cols: opts.cols,
          rows: opts.rows,
          pinned: opts.pinned ?? false,
          player,
          custom: {},
        },
        { atStart: opts.atStart ?? false },
      );
    });
    shellInits.delete(id);
    const mod = moduleElementOf(id);
    if (!mod) throw new Error(`Module ${opts.title} didn't render`);
    // The shell doesn't read `_`-state, so after its render and before the body's first
    // one is the earliest the element exists and still in time.
    if (opts.custom) Object.assign(mod, opts.custom);
    // A module made showing a team (a team dropped on empty canvas, Undo, a saved dashboard): its
    // search box already named its player, before the team went on (Ben, 2026-10-30: a League
    // Table row dropped as a Bio read "Declan Rice").
    const team = teamOfModule(mod);
    const box = mod.querySelector<HTMLInputElement>(".player-search");
    if (team && box) box.value = team.name;

    // main.js's order: watch the strip's overflow, render the body, then grow the canvas to
    // fit. (main.js also wired the phone reorder handle here — gone with the mobile JS, 05k-1.)
    watchControlsOverflow(mod);
    refreshModuleBody(mod);
    ensureCanvasPadding();
    return mod;
  }

  function removeModule(mod: HTMLElement): void {
    const id = idOf(mod);
    // Already gone (e.g. an Undo rebuilt the canvas mid-animation): nothing to do, as
    // main.js's mod.remove() on a detached node did nothing.
    if (id === undefined) return;
    flushSync(() => store.getState().removeModule(id));
  }

  function closeModule(mod: HTMLElement): void {
    // A map's loose panel (map-panels.ts): closing the panel puts it back in its map; closing the
    // map closes its panel. Same step (the caller's checkpoint).
    if (isPanelModule(mod)) {
      const map = reattachPanel(mod);
      if (map) refreshModuleBody(map);
    } else {
      for (const kind of ["details", "players"] as const) {
        const panel = panelPartner(mod, kind);
        if (panel && !panel.classList.contains("module-closing")) {
          setModState(panel, "_panelLink", undefined);
          closeModule(panel);
        }
      }
    }
    // A widget that shows its own tooltip as the mouse moves over it (Radar wedges, the
    // maps, the scatter…) only hides it when the mouse LEAVES — and a module deleted under
    // the mouse never gets that, so its tooltip stayed on the canvas for good (Ben,
    // 2026-10-02). Hide it as the module goes.
    hideTooltip();
    // Convention: a renderer that attaches its own ResizeObserver stores it on the
    // observed element as `_resizeObserver` so it's disconnected here.
    mod.querySelectorAll("*").forEach((el) => {
      (el as unknown as { _resizeObserver?: ResizeObserver })._resizeObserver?.disconnect();
    });
    mod.classList.add("module-closing");
    // Any animation ending inside the module removes it — main.js's behaviour, kept.
    mod.addEventListener("animationend", () => removeModule(mod), { once: true });
  }

  function clearModules(): void {
    hideTooltip(); // same reason as closeModule (Clear, an Undo rebuild, a dashboard load)
    flushSync(() => store.getState().clearModules());
  }

  function setModuleTitle(mod: HTMLElement, title: string): void {
    const id = idOf(mod);
    if (id === undefined) throw new Error(`setModuleTitle: not a live module (${title})`);
    // Synchronous so the refresh that follows reads the new title.
    flushSync(() => store.getState().updateModule(id, { title }));
  }

  function togglePin(mod: HTMLElement): void {
    const id = idOf(mod);
    const module = store.getState().modules.find((m) => m.id === id);
    if (id === undefined || !module) throw new Error("togglePin: not a live module");
    saveState();
    flushSync(() => store.getState().updateModule(id, { pinned: !module.pinned }));
    if (module.pinned) return; // unpinned — no animation
    const pinBtn = mod.querySelector<HTMLElement>(".module-pin");
    if (!pinBtn) throw new Error("togglePin: .module-pin missing");
    pinBtn.classList.remove("pin-animating");
    void pinBtn.offsetWidth; // force a reflow so re-pinning replays the animation
    pinBtn.classList.add("pin-animating");
    pinBtn.addEventListener("animationend", () => pinBtn.classList.remove("pin-animating"), { once: true });
  }

  function closeFromHeader(mod: HTMLElement): void {
    saveState();
    closeModule(mod);
  }

  function deleteSelected(): boolean {
    const selected = selectedModules();
    if (!selected.length) return false;
    saveState(); // one step for the whole batch — a single Undo brings them all back
    selected.forEach((mod) => closeModule(mod));
    return true;
  }

  return {
    ...createPlayerActions(store, saveState, refreshModuleBody, idOf),
    ...selectionApi,
    ...canvasSpaceApi,
    setWorkspaceShift: (x, y) => store.getState().setWorkspaceShift(x, y),
    createModule,
    closeModule,
    removeModule,
    clearModules,
    refreshModuleBody,
    setModuleTitle,
    togglePin,
    closeFromHeader,
    deleteSelected,
  };
}

/** Wire the shell actions into the page (where main.js loaded, before tour.js). */
export function installCanvas(store: ProtoStore, saveState: () => void): CanvasApi {
  installed = createCanvasApi(store, saveState);
  const workspace = document.getElementById("workspace");
  if (!workspace) throw new Error("#workspace missing from legacy-scaffold.html");
  installLinkHints(workspace);
  installCanvasSpace(workspace);
  return installed;
}
